/**
 * Moving the open map to schema v2 (docs/plans/2026-10-02-crdt-schema-v2.md,
 * decision 5): automatically when a map is opened — once synced, or once
 * loaded for a map kept on this device only — by a member who can write
 * (CRDTBridge.migrateMapToV2 says no to a viewer). Maps nobody opens are
 * converted in the background (background-conversion.js).
 *
 * A backup of the doc first (IndexedDB `orbital-migration-backups`, as the
 * notes migrations; dropped once done), then one transaction that leaves
 * the v1 data as it is. Measured on synthetic maps: 67 ms for 5,000 nodes,
 * 200 ms for 20,000; a card shows only from LARGE_MAP nodes.
 */
import { beginBlockingOperation } from "../host.js";
import { t } from "../../shared/i18n/index.js";
import { backupStore } from "./migration-backups.js";

const LARGE_MAP = 20000;

/** Keep a copy of a map's state before converting it (dropped once the server has the result). */
export async function saveFormatBackup(docId, bytes) {
  try {
    if (docId && bytes) await backupStore("readwrite", (store) => store.put(bytes, `${docId}:schema-v2`));
  } catch (err) {
    // The v1 data stays in the map whatever happens; the backup is extra.
    console.warn("[Migration] Could not save the backup before the map format update:", err);
  }
}

export async function dropFormatBackup(docId) {
  try { await backupStore("readwrite", (store) => store.delete(`${docId}:schema-v2`)); } catch { /* kept: harmless */ }
}

/**
 * @param {Object} bridge - CRDTBridge
 * @returns {Promise<{migrated: boolean, count: number, skipped?: string}|null>}
 */
export async function migrateOpenMapToV2(bridge) {
  if (!bridge?.migrateMapToV2 || bridge.isMapV2?.()) return null;
  const docId = bridge.getCurrentDocId?.();
  await saveFormatBackup(docId, bridge.createDocBackup?.());
  const count = bridge.getMapNodeCount?.() || 0;
  const large = count >= LARGE_MAP;
  // A large map: the person waits, told why (the interface shows it).
  const waiting = large ? beginBlockingOperation({
    title: t("sync.mapFormat.title"),
    intro: t("sync.mapFormat.intro"),
    preparing: t("sync.mapFormat.progress", { count }),
    done: t("account.migration.done"),
    failed: t("sync.mapFormat.failed"),
  }) : null;
  await waiting?.paint();
  try {
    const result = bridge.migrateMapToV2();
    waiting?.finish(true);
    if (result?.migrated) {
      console.log(`[Migration] Map ${docId} moved to schema v2 (${result.count} nodes)`);
      await dropFormatBackup(docId);
    }
    return result;
  } catch (err) {
    console.error("[Migration] Map format update failed; the map stays in its old format:", err);
    waiting?.finish(false);
    return { migrated: false, count: 0, skipped: "error" };
  }
}
